import { useParams, Navigate, Link } from "react-router-dom";
import { InsightsNavbar } from "@/components/InsightsNavbar";
import { Footer } from "@/components/Footer";
import { ScrollProgressIndicator } from "@/components/ScrollProgressIndicator";
import { ToolAccessGate } from "@/components/ToolAccessGate";
import { SEO, createToolSchema, createFAQSchema, createHowToSchema } from "@/components/SEO";
import { RelatedInsights } from "@/components/RelatedInsights";
import { ToolUsageGuide } from "@/components/ToolUsageGuide";
import { GmailPromotionsAnalyzer } from "@/components/insights/gmail-promotions/GmailPromotionsAnalyzer";
import { SubjectLineAnalyzer } from "@/components/tools/SubjectLineAnalyzer";
import { EmailAuthChecker } from "@/components/tools/EmailAuthChecker";
import { BlacklistChecker } from "@/components/tools/BlacklistChecker";
import { EmailPreviewTool } from "@/components/tools/EmailPreviewTool";
import { EmailHeaderAnalyzer } from "@/components/tools/EmailHeaderAnalyzer";
import { EmailContentAnalyzer } from "@/components/tools/EmailContentAnalyzer";
import { EmailChecker } from "@/components/tools/EmailChecker";
import { SeoMetaAnalyzer } from "@/components/tools/SeoMetaAnalyzer";
import { PhishingDetector } from "@/components/tools/PhishingDetector";
import { RobotsMetaChecker } from "@/components/tools/RobotsMetaChecker";
import { DomainAgeChecker } from "@/components/tools/DomainAgeChecker";
import { LinkValidator } from "@/components/tools/LinkValidator";
import { DmarcReportAnalyzer } from "@/components/tools/DmarcReportAnalyzer";
import { M365TenantAnalyzer } from "@/components/tools/M365TenantAnalyzer";
import { BulkSubjectLineAnalyzer } from "@/components/tools/BulkSubjectLineAnalyzer";
import { DnsPropagationChecker } from "@/components/tools/DnsPropagationChecker";
import { PMCostCalculator } from "@/components/tools/PMCostCalculator";
import { MeetingCostCalculator } from "@/components/tools/MeetingCostCalculator";
import { CollaborationComparison } from "@/components/tools/CollaborationComparison";
import { GlobalLatencyTester } from "@/components/tools/GlobalLatencyTester";
import { WebhookDebugger } from "@/components/tools/WebhookDebugger";
import { ApiResponseSimulator } from "@/components/tools/ApiResponseSimulator";
import { BounceRateCalculator } from "@/components/tools/BounceRateCalculator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Wrench, Home, ChevronRight } from "lucide-react";
import { getToolEnhancement } from "@/data/toolFAQs";
import { useMemo } from "react";

// Tool configuration with SEO metadata
const toolsConfig: Record<string, {
  title: string;
  description: string;
  category: string;
  component: React.ReactNode;
  keywords?: string[];
}> = {
  "email-checker": {
    title: "Email Deliverability Tester",
    description: "Send a test email and get a comprehensive deliverability report with SPF, DKIM, DMARC analysis and spam score.",
    category: "Deliverability",
    component: <EmailChecker />,
    keywords: ["email deliverability", "spam test", "inbox placement", "email testing"],
  },
  "auth-checker": {
    title: "SPF/DKIM/DMARC Checker",
    description: "Verify email authentication DNS records instantly. Check SPF, DKIM, and DMARC configuration for any domain.",
    category: "Authentication",
    component: <EmailAuthChecker />,
    keywords: ["SPF checker", "DKIM checker", "DMARC checker", "email authentication"],
  },
  "blacklist-checker": {
    title: "IP & Domain Blacklist Scanner",
    description: "Check if your IP or domain is listed on major email blocklists that could affect deliverability.",
    category: "Authentication",
    component: <BlacklistChecker />,
    keywords: ["blacklist checker", "IP reputation", "domain blocklist", "email blocklist"],
  },
  "header-analyzer": {
    title: "Email Header Decoder",
    description: "Debug email deliverability issues with AI-powered header analysis. Decode routing, authentication, and spam indicators.",
    category: "Deliverability",
    component: <EmailHeaderAnalyzer />,
    keywords: ["email headers", "header analyzer", "email debugging", "routing analysis"],
  },
  "tab-predictor": {
    title: "Gmail Tab Predictor",
    description: "Predict whether your email will land in Primary or Promotions tab in Gmail with AI analysis.",
    category: "Deliverability",
    component: <GmailPromotionsAnalyzer />,
    keywords: ["Gmail tabs", "promotions tab", "primary inbox", "Gmail deliverability"],
  },
  "content-analyzer": {
    title: "Accessibility & Mobile Checker",
    description: "AI-powered analysis of email accessibility and mobile rendering compatibility.",
    category: "Content",
    component: <EmailContentAnalyzer />,
    keywords: ["email accessibility", "mobile email", "responsive email", "ADA compliance"],
  },
  "email-preview": {
    title: "Email Client Preview",
    description: "See how your email renders across Gmail, Outlook, Apple Mail and other popular email clients.",
    category: "Content",
    component: <EmailPreviewTool />,
    keywords: ["email preview", "email rendering", "email client testing", "responsive preview"],
  },
  "subject-analyzer": {
    title: "Spam Trigger Analyzer",
    description: "AI detection of spam triggers in subject lines or email body. Avoid spam filters with smart analysis.",
    category: "Content",
    component: <SubjectLineAnalyzer />,
    keywords: ["spam triggers", "subject line analyzer", "spam words", "email spam"],
  },
  "bulk-subject-analyzer": {
    title: "Bulk Subject Line Analyzer",
    description: "Compare multiple subject lines side-by-side and find the best performer for your email campaigns.",
    category: "Content",
    component: <BulkSubjectLineAnalyzer />,
    keywords: ["A/B testing", "subject line comparison", "email optimization", "bulk analysis"],
  },
  "seo-meta-analyzer": {
    title: "SEO Meta Tag Analyzer",
    description: "Optimize page titles, meta descriptions, and Open Graph tags for better search visibility.",
    category: "SEO",
    component: <SeoMetaAnalyzer />,
    keywords: ["SEO analyzer", "meta tags", "Open Graph", "search optimization"],
  },
  "robots-checker": {
    title: "Robots Meta Checker",
    description: "Analyze robots directives, meta robots tags, and their impact on search engine indexing.",
    category: "SEO",
    component: <RobotsMetaChecker />,
    keywords: ["robots.txt", "meta robots", "noindex", "search indexing"],
  },
  "phishing-detector": {
    title: "Phishing Email Detector",
    description: "Analyze suspicious emails for phishing indicators, scam patterns, and security threats.",
    category: "Security",
    component: <PhishingDetector />,
    keywords: ["phishing detection", "email security", "scam analyzer", "fraud detection"],
  },
  "domain-age-checker": {
    title: "Domain Age Checker",
    description: "Look up domain registration date via WHOIS. Check domain age for trust and reputation assessment.",
    category: "Authentication",
    component: <DomainAgeChecker />,
    keywords: ["domain age", "WHOIS lookup", "domain registration", "domain history"],
  },
  "link-validator": {
    title: "Link Validator",
    description: "Check URLs for safety, redirect chains, and blocklist status. Validate links before sending.",
    category: "Security",
    component: <LinkValidator />,
    keywords: ["link checker", "URL validator", "redirect checker", "safe browsing"],
  },
  "dmarc-analyzer": {
    title: "DMARC Report Analyzer",
    description: "Parse and visualize DMARC aggregate reports. Understand authentication failures and sources.",
    category: "Authentication",
    component: <DmarcReportAnalyzer />,
    keywords: ["DMARC reports", "aggregate reports", "email forensics", "authentication reports"],
  },
  "m365-tenant-analyzer": {
    title: "Google/Microsoft 365 Analyzer",
    description: "Find tenant information, all associated domains, and DNS records for Google Workspace or Microsoft 365.",
    category: "Authentication",
    component: <M365TenantAnalyzer />,
    keywords: ["Microsoft 365", "Google Workspace", "tenant lookup", "domain discovery"],
  },
  "dns-propagation-checker": {
    title: "DNS Propagation Checker",
    description: "Check if DNS record changes have propagated globally across different nameservers.",
    category: "Authentication",
    component: <DnsPropagationChecker />,
    keywords: ["DNS propagation", "DNS checker", "nameserver lookup", "DNS records"],
  },
  "pm-cost-calculator": {
    title: "PM Tool Cost Calculator",
    description: "Compare project management tool costs by team size. Find the most cost-effective solution for your team.",
    category: "Productivity",
    component: <PMCostCalculator />,
    keywords: ["project management", "tool comparison", "cost calculator", "team tools"],
  },
  "meeting-cost-calculator": {
    title: "Meeting Cost Calculator",
    description: "Calculate the true cost of meetings to your organization based on attendees, duration, and salaries.",
    category: "Productivity",
    component: <MeetingCostCalculator />,
    keywords: ["meeting cost", "productivity", "time management", "meeting ROI"],
  },
  "collaboration-comparison": {
    title: "Team Collaboration Comparison",
    description: "Compare Slack, Teams, Notion and 8+ collaboration tools. Find the best fit for your team.",
    category: "Productivity",
    component: <CollaborationComparison />,
    keywords: ["Slack vs Teams", "collaboration tools", "team communication", "workplace tools"],
  },
  "global-latency-tester": {
    title: "Global Latency Tester",
    description: "Test website response times from edge servers. Measure latency and performance consistency.",
    category: "Developer",
    component: <GlobalLatencyTester />,
    keywords: ["latency test", "website speed", "performance testing", "response time"],
  },
  "webhook-debugger": {
    title: "Webhook Debugger",
    description: "Generate a temporary URL to catch and inspect incoming webhooks in real-time. Debug integrations easily.",
    category: "Developer",
    component: <WebhookDebugger />,
    keywords: ["webhook testing", "webhook debugger", "API debugging", "integration testing"],
  },
  "api-response-simulator": {
    title: "API Response Simulator",
    description: "Create mock API endpoints with custom responses for frontend testing without a real backend.",
    category: "Developer",
    component: <ApiResponseSimulator />,
    keywords: ["API mocking", "mock server", "frontend testing", "API simulation"],
  },
  "bounce-rate-calculator": {
    title: "Bounce Rate Calculator",
    description: "Estimate email bounce rates based on list age, verification status, engagement, and source quality.",
    category: "Deliverability",
    component: <BounceRateCalculator />,
    keywords: ["bounce rate", "email list health", "email verification", "list hygiene"],
  },
};

const ToolPage = () => {
  const { toolId } = useParams<{ toolId: string }>();
  
  const tool = toolId ? toolsConfig[toolId] : undefined;
  const enhancement = toolId ? getToolEnhancement(toolId) : undefined;

  // Build JSON-LD schemas array: Tool + optional FAQ + optional HowTo
  const jsonLdSchemas = useMemo(() => {
    if (!tool) return [];
    const schemas: Record<string, any>[] = [
      createToolSchema({ name: tool.title, description: tool.description, category: tool.category }),
    ];
    if (enhancement?.faqs?.length) {
      schemas.push(createFAQSchema(enhancement.faqs));
    }
    if (enhancement?.howToUse?.steps?.length) {
      schemas.push(createHowToSchema({
        name: `How to Use ${tool.title}`,
        description: tool.description,
        steps: enhancement.howToUse.steps.map((step, i) => ({ name: `Step ${i + 1}`, text: step })),
      }));
    }
    return schemas;
  }, [toolId, tool, enhancement]);

  if (!toolId || !tool) {
    return <Navigate to="/tools" replace />;
  }

  const pageTitle = `${tool.title} - Free Online Tool | IT Dukes`;
  const pageDescription = tool.description;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={[...(tool.keywords || []), "free tool", "online tool", "IT Dukes"]}
        canonical={`/tools/${toolId}`}
        jsonLd={jsonLdSchemas}
      />
      <InsightsNavbar showBack />
      <ScrollProgressIndicator />

      <main className="page-container py-8 sm:py-12">
        {/* Enhanced Breadcrumb */}
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 text-sm text-muted-foreground" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link 
                to="/" 
                className="flex items-center gap-1 hover:text-foreground transition-colors"
                itemProp="item"
              >
                <Home className="w-3.5 h-3.5" />
                <span itemProp="name">Home</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <ChevronRight className="w-3.5 h-3.5" />
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link 
                to="/tools" 
                className="hover:text-foreground transition-colors"
                itemProp="item"
              >
                <span itemProp="name">Tools</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
            <ChevronRight className="w-3.5 h-3.5" />
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-foreground font-medium truncate max-w-[200px]" itemProp="name">
                {tool.title}
              </span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        {/* Tool Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <Wrench className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{tool.title}</h1>
              <p className="text-muted-foreground">{tool.category}</p>
            </div>
          </div>
          <p className="text-muted-foreground max-w-2xl">
            {tool.description}
          </p>
        </div>

        {/* Tool Content */}
        <ToolAccessGate toolId={toolId} toolName={tool.title}>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                {tool.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tool.component}
            </CardContent>
          </Card>
          
          {/* Usage Guide & FAQs */}
          <ToolUsageGuide toolId={toolId} />
        </ToolAccessGate>

        {/* Related Insights Section */}
        <RelatedInsights toolId={toolId} />

        {/* Back to Tools */}
        <div className="mt-8 text-center">
          <Button variant="outline" asChild>
            <Link to="/tools" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to All Tools
            </Link>
          </Button>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default ToolPage;
